import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CreditCard } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

const SubscriptionBanner = () => {
  const { subscriptionInfo, currentWorkspace } = useWorkspace();
  if (!subscriptionInfo) return null;

  const {
    subscription,
    subscription_expired,
    can_create_workspace,
    is_super_admin,
    workspace_comped_lifetime,
    is_lifetime_plan
  } = subscriptionInfo;
  if (is_super_admin || workspace_comped_lifetime || is_lifetime_plan) return null;

  const canManageBillingUi =
    ['owner', 'admin'].includes(String(currentWorkspace?.user_role || '').toLowerCase());

  const status = subscription?.status;

  const trialDaysLeft = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const days = status === 'trial' && subscription?.trial_end ? trialDaysLeft(subscription.trial_end) : null;

  // Expired
  if (subscription_expired || status === 'expired') {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Your subscription has expired. Renew to continue using all features.</span>
          <Link
            to="/billing"
            className="font-semibold text-amber-700 hover:text-amber-900 underline"
          >
            Renew now
          </Link>
        </div>
      </div>
    );
  }

  // Failed / past_due
  if (status === 'failed' || status === 'past_due') {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Payment failed. Update your payment method to avoid service interruption.</span>
          <Link
            to="/billing"
            className="font-semibold text-red-700 hover:text-red-900 underline"
          >
            Update payment
          </Link>
        </div>
      </div>
    );
  }

  // Trial - show when &lt; 3 days left
  if (status === 'trial' && days !== null && days <= 3) {
    return (
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-sm text-blue-800">
          <CreditCard className="w-4 h-4 flex-shrink-0" />
          <span>
            Trial ends in {days} day{days !== 1 ? 's' : ''}. Add a payment method to continue.
          </span>
          <Link
            to="/billing"
            className="font-semibold text-blue-700 hover:text-blue-900 underline"
          >
            Add payment
          </Link>
        </div>
      </div>
    );
  }

  // At workspace limit — only show upgrade CTA to owner/admin (members use workspace; they don’t manage account billing here)
  const planSlug = subscriptionInfo?.plan?.slug;
  const isInternalLifetime = planSlug === 'internal-lifetime';

  if (can_create_workspace === false && canManageBillingUi && !isInternalLifetime) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>You&apos;ve reached your workspace limit.</span>
          <Link
            to="/billing"
            className="font-semibold text-amber-700 hover:text-amber-900 underline"
          >
            Upgrade plan
          </Link>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
